import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchIndex, highlightRanges, POPULAR_LINKS, type SearchDoc, type SearchType } from '../../lib/siteSearch';
import { openChat } from '../../lib/chat';
import { trackConversion } from '../../lib/publicSettings';

const RECENTS_KEY = 'cendien_search_recents';

const GROUP_ORDER: SearchType[] = [
  'Services',
  'Industries',
  'Insights',
  'Resources',
  'Case Studies',
  'Careers',
  'Company',
];

const GROUP_META: Record<SearchType, { icon: string; cls: string }> = {
  Services: { icon: 'ri-tools-line', cls: 'bg-teal-50 text-teal-700' },
  Industries: { icon: 'ri-building-4-line', cls: 'bg-amber-50 text-amber-700' },
  Insights: { icon: 'ri-article-line', cls: 'bg-slate-100 text-slate-600' },
  Resources: { icon: 'ri-book-open-line', cls: 'bg-teal-50 text-teal-700' },
  'Case Studies': { icon: 'ri-bar-chart-box-line', cls: 'bg-amber-50 text-amber-700' },
  Careers: { icon: 'ri-briefcase-line', cls: 'bg-amber-50 text-amber-700' },
  Company: { icon: 'ri-information-line', cls: 'bg-slate-100 text-slate-600' },
};

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string').slice(0, 6) : [];
  } catch {
    return [];
  }
}

function writeRecents(list: string[]) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, 6)));
  } catch {
    /* ignore */
  }
}

function Highlight({ text, query }: { text: string; query: string }) {
  const parts = highlightRanges(text, query);
  return (
    <>
      {parts.map((p, i) =>
        p.match ? (
          <mark key={i} className="bg-teal-100 text-teal-900 rounded-[3px] px-0.5">
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  const { search } = useSearchIndex();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const trackedRef = useRef('');

  const results = useMemo(() => (query.trim() ? search(query, 24) : []), [query, search]);

  const grouped = useMemo(() => {
    const map: Partial<Record<SearchType, SearchDoc[]>> = {};
    results.forEach((r) => {
      if (!map[r.type]) map[r.type] = [];
      (map[r.type] as SearchDoc[]).push(r);
    });
    return GROUP_ORDER.filter((t) => map[t]).map((t) => ({ type: t, items: map[t] as SearchDoc[] }));
  }, [results]);

  // Track zero-result searches (debounced)
  useEffect(() => {
    if (!isOpen) return;
    const q = query.trim();
    if (q.length < 2) return;
    const t = setTimeout(() => {
      if (results.length === 0 && trackedRef.current !== `zero:${q}`) {
        trackedRef.current = `zero:${q}`;
        trackConversion('search_no_results', { search_term: q });
      }
    }, 700);
    return () => clearTimeout(t);
  }, [query, results.length, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setRecents(readRecents());
      setActiveIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    setQuery('');
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const commitRecent = useCallback((term: string) => {
    const clean = term.trim();
    if (!clean) return;
    const next = [clean, ...readRecents().filter((r) => r.toLowerCase() !== clean.toLowerCase())].slice(0, 6);
    writeRecents(next);
    setRecents(next);
  }, []);

  const go = useCallback(
    (doc: SearchDoc) => {
      const q = query.trim();
      if (q) {
        commitRecent(q);
        if (trackedRef.current !== `q:${q}`) {
          trackedRef.current = `q:${q}`;
          trackConversion('search', { search_term: q, result_type: doc.type });
        }
      }
      navigate(doc.url);
      onClose();
    },
    [navigate, onClose, query, commitRecent],
  );

  const askAssistant = useCallback(() => {
    const q = query.trim();
    if (q) commitRecent(q);
    onClose();
    openChat(q || undefined);
  }, [query, onClose, commitRecent]);

  const viewAll = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    commitRecent(q);
    if (trackedRef.current !== `q:${q}`) {
      trackedRef.current = `q:${q}`;
      trackConversion('search', { search_term: q, result_type: 'all' });
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
    onClose();
  }, [query, navigate, onClose, commitRecent]);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[activeIndex]) go(results[activeIndex]);
        else if (query.trim()) viewAll();
      }
    },
    [isOpen, results, activeIndex, go, viewAll, onClose, query],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]') as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onMouseDown={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '78vh' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <span className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-search-line text-slate-400 text-xl"></i>
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search services, industries, insights, careers…"
            className="flex-1 text-base text-slate-800 placeholder-slate-400 outline-none bg-transparent"
            aria-label="Search query"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-slate-500 text-sm"></i>
            </button>
          )}
          <kbd className="hidden sm:flex items-center px-2 py-1 text-xs text-slate-400 bg-slate-100 rounded-lg font-mono shrink-0">ESC</kbd>
        </div>

        {/* Body */}
        <div ref={listRef} className="overflow-y-auto flex-1">
          {!query.trim() ? (
            <div className="p-5">
              {recents.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Recent</p>
                  <div className="flex flex-wrap gap-2">
                    {recents.map((r) => (
                      <button
                        key={r}
                        onClick={() => setQuery(r)}
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-history-line text-slate-400"></i>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Popular</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {POPULAR_LINKS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => go(item)}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 text-left group transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600 shrink-0">
                      <i className={item.icon}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-700 group-hover:text-teal-700 truncate">{item.title}</span>
                      <span className="block text-xs text-slate-400 truncate">{item.description}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
                <i className="ri-search-eye-line text-slate-400 text-2xl"></i>
              </div>
              <p className="text-base font-semibold text-slate-700 mb-1">No results for &ldquo;{query}&rdquo;</p>
              <p className="text-sm text-slate-400 mb-5">Try different keywords, or ask our assistant directly.</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={askAssistant}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-chat-3-line"></i>
                  No results — ask our assistant
                </button>
                <button
                  onClick={viewAll}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-list-check-2"></i>
                  View all results
                </button>
              </div>
            </div>
          ) : (
            <div className="py-3">
              {grouped.map(({ type, items }) => {
                const offset = results.indexOf(items[0]);
                return (
                  <div key={type} className="mb-1">
                    <div className="px-5 py-1.5 flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${GROUP_META[type].cls}`}>
                        <i className={GROUP_META[type].icon}></i>
                        {type}
                      </span>
                    </div>
                    {items.map((item, idx) => {
                      const globalIdx = offset + idx;
                      const isActive = globalIdx === activeIndex;
                      return (
                        <button
                          key={item.id}
                          data-active={isActive}
                          onClick={() => go(item)}
                          onMouseEnter={() => setActiveIndex(globalIdx)}
                          className={`w-full flex items-start gap-3 px-5 py-3 text-left transition-colors cursor-pointer ${isActive ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                        >
                          <span className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${isActive ? 'bg-teal-100 text-teal-600' : 'bg-slate-100 text-slate-500'}`}>
                            <i className={item.icon}></i>
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className={`block text-sm font-semibold leading-snug ${isActive ? 'text-teal-700' : 'text-slate-800'}`}>
                              <Highlight text={item.title} query={query} />
                            </span>
                            <span className="block text-xs text-slate-500 mt-0.5 line-clamp-1">
                              <Highlight text={item.description} query={query} />
                            </span>
                          </span>
                          {isActive && (
                            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-1 text-teal-500">
                              <i className="ri-arrow-right-line text-sm"></i>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {query.trim() && results.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 bg-slate-50/60">
            <p className="text-xs text-slate-400">
              {results.length} result{results.length !== 1 ? 's' : ''} · <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[11px] font-mono">↑↓</kbd> navigate · <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[11px] font-mono">↵</kbd> open
            </p>
            <div className="flex items-center gap-3">
              <button onClick={askAssistant} className="text-xs font-semibold text-slate-500 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">
                Ask assistant
              </button>
              <button onClick={viewAll} className="text-xs font-semibold text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap">
                View all results →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GlobalSearch;